// TODO: sửa lại chỉ dùng 1 biến màu và lắng nghe nó thay đổi để lấy mấy cái thông tin cần thiết khác
'use client'

import { useAddToCart } from '@/hooks/react-query/cart/use-cart'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent, TTrackingEcommerce } from '@/lib/gtm/type'
import { trackPixelEvent } from '@/lib/tracking/track-pixel'
import { stringToSlug } from '@/lib/utils'
import { Option, ProductOptionsValue, VariantMapped } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

export interface ProductDetailContextType {
  product: ProductDetailFormatted | null
  quantity: number
  sizeSelected: string | null
  colorSelected: string | null
  variantActive: VariantMapped | null
  colorOptionActive: Option | null
  sizeOptions: string[]
  sortedColorOptions: Option[]
  setColorSelected: (color: string) => void
  setSizeSelected: (size: string) => void
  setColorOptionActive: (colorOption: Option) => void
  setVariantActive: (variant: VariantMapped) => void
  setProduct: (product: ProductDetailFormatted) => void
  setQuantity: (quantity: number) => void
  handleAddToCart: (payload: AddToCartPayload) => void
  isPendingAddToCart: boolean
  comboSelectedInfo: {
    option1: string
    option2: string
  }
  setComboSelectedInfo: (info: { option1: string; option2: string }) => void
}

const ProductDetailContext = createContext<ProductDetailContextType>({
  product: null,
  quantity: 1,
  sizeSelected: null,
  colorSelected: null,
  variantActive: null,
  colorOptionActive: null,
  sizeOptions: [],
  sortedColorOptions: [],
  setColorSelected: () => {},
  setSizeSelected: () => {},
  setColorOptionActive: () => {},
  setVariantActive: () => {},
  setProduct: () => {},
  handleAddToCart: () => {},
  setQuantity: () => {},
  isPendingAddToCart: false,
  comboSelectedInfo: {
    option1: '',
    option2: '',
  },
  setComboSelectedInfo: () => {},
})

export const useProductDetail = () => {
  const context = useContext(ProductDetailContext)
  if (!context) {
    throw new Error(
      'useProductDetail must be used within ProductDetailProvider'
    )
  }
  return context
}

// Thêm hook bao bọc để đảm bảo product không null
export const useProductDetailSafe = () => {
  const { product, ...rest } = useProductDetail()

  if (!product) {
    throw new Error('Product is not available')
  }

  return { product, ...rest }
}

interface ProductDetailProviderProps {
  children: React.ReactNode
  initialProduct: ProductDetailFormatted
}

interface AddToCartPayload {
  regularPrice: number
  comparePrice: number
  giftId?: string
  giftOptionId?: string
}

export const ProductDetailProvider = ({
  children,
  initialProduct,
}: ProductDetailProviderProps) => {
  const { mutateAsync: addToCart, isPending: isPendingAddToCart } =
    useAddToCart()
  const trackEvent = useTrackEvent<Partial<TTrackingEcommerce>>()

  const [product, setProduct] = useState<ProductDetailFormatted>(initialProduct)
  const [variantActive, setVariantActive] = useState<VariantMapped | null>(null)
  const [colorOptionActive, setColorOptionActive] = useState<Option | null>(
    null
  )
  const [colorSelected, setColorSelected] = useState<string>('')
  const [sizeSelected, setSizeSelected] = useState<string>('')
  const [quantity, setQuantity] = useState<number>(1)
  const [comboSelectedInfo, setComboSelectedInfo] = useState<{
    option1: string
    option2: string
  }>({
    option1: initialProduct?.combo?.options[0]?.values?.[0]?.value || '',
    option2: initialProduct?.combo?.options[1]?.values?.[0]?.value || '',
  })

  // Sắp xếp kích thước theo thứ tự XS, S, M, L, XL, ...
  const sizeOptions = useMemo(() => {
    const allVariants = Object.values(product.mapped_variants || {}).flat()

    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL']
    const sizeOrderMap = new Map(sizeOrder.map((size, index) => [size, index]))

    const sizeSet = new Set<string>()

    for (const variant of allVariants) {
      if (variant.size) sizeSet.add(variant.size)
    }

    return Array.from(sizeSet).sort((a, b) => {
      const indexA = sizeOrderMap.get(a) ?? Infinity
      const indexB = sizeOrderMap.get(b) ?? Infinity
      return indexA - indexB
    })
  }, [product.mapped_variants])

  // Sắp xếp màu sắc theo thứ tự từ options
  const sortedColorOptions = useMemo(() => {
    const colorOptions =
      product?.options_value?.find((option: ProductOptionsValue) =>
        option?.options_id?.includes('color')
      )?.options || []

    // Giữ nguyên thứ tự từ API
    return colorOptions
  }, [product?.options_value])

  // Tự động chọn size đầu tiên khi màu thay đổi
  useEffect(() => {
    if (colorSelected && product.mapped_variants) {
      const colorKey = Object.keys(product.mapped_variants).find(
        (key) => stringToSlug(key) === colorSelected
      )

      if (colorKey && product.mapped_variants[colorKey].length > 0) {
        const availableVariant = product.mapped_variants[colorKey].find(
          (variant: VariantMapped) => !variant.isDisable
        )

        if (availableVariant && availableVariant.size) {
          setSizeSelected(availableVariant.size)
        }
      }
    }
  }, [colorSelected, product.mapped_variants])

  const comboAddToCart = (payload: AddToCartPayload) => {
    const { giftId, giftOptionId } = payload

    const comboSelected = product?.combo?.variants.find((item) => {
      return (
        item.option1 === comboSelectedInfo.option1 &&
        item.option2 === comboSelectedInfo.option2
      )
    })

    if (product?.is_combo && !comboSelected) {
      console.warn('Không tìm thấy variant phù hợp', {
        comboSelectedInfo,
      })
      return
    }

    addToCartSync({
      product_id: product?.id || '',
      quantity,
      options: {
        id: comboSelected?.id || '',
      },
      prize_when_buy_with_collection: {
        product_id: giftId || '',
        options: {
          id: giftOptionId || '',
        },
      },
      product_name: product.title,
      variant_name: `${comboSelected?.option1 || ''} / ${comboSelected?.option2 || ''}`,
      regular_price: product.regular_price,
      compare_price: product.compare_price || 0,
      image_url: product.default_thumbnail || '',
    })
  }

  const handleAddToCart = (payload: AddToCartPayload) => {
    if (product?.is_combo) {
      comboAddToCart(payload)
    } else {
      handleAddToCartNormal(payload)
    }
  }

  const handleAddToCartNormal = (payload: AddToCartPayload) => {
    const { giftId, giftOptionId } = payload

    const mappedVariants = product?.mapped_variants
    if (!mappedVariants) {
      console.warn('No mapped variants found')
      return
    }

    const colorKey = Object.keys(mappedVariants).find(
      (key) => stringToSlug(key) === colorSelected
    )

    if (!colorKey) {
      console.warn('Color không tồn tại trong mapped_variants', {
        colorSelected,
        availableColors: Object.keys(mappedVariants).map((key) =>
          stringToSlug(key)
        ),
      })
      return
    }

    const selectedVariant = mappedVariants[colorKey].find(
      (item) => item.size === sizeSelected
    )

    if (!selectedVariant) {
      console.warn('Không tìm thấy variant phù hợp', {
        colorKey,
        sizeSelected,
        availableSizes: mappedVariants[colorKey].map((v) => v.size),
      })
      return
    }

    // TODO: Add to cart logic here
    addToCartSync({
      product_id: selectedVariant.product_id,
      quantity,
      options: {
        id: selectedVariant?.id,
      },
      prize_when_buy_with_collection: {
        product_id: giftId || '',
        options: {
          id: giftOptionId || '',
        },
      },
      product_name: product.title,
      variant_name: `${selectedVariant?.color || ''} / ${selectedVariant?.size || ''}`,
      regular_price: payload.regularPrice || product.regular_price,
      compare_price: payload.comparePrice || product.compare_price || 0,
      image_url: selectedVariant.thumbnail || product.default_thumbnail || '',
    })
  }

  const addToCartSync = async ({
    product_id,
    quantity,
    options,
    prize_when_buy_with_collection,
    product_name,
    variant_name,
    regular_price,
    compare_price,
    image_url,
  }: {
    product_id: string
    quantity: number
    options: {
      id: string
    }
    prize_when_buy_with_collection: {
      product_id: string
      options: {
        id: string
      }
    }
    product_name: string
    variant_name: string
    regular_price: number
    compare_price: number
    image_url: string
  }) => {
    await addToCart({
      payload: {
        product_id,
        quantity,
        options: {
          id: options.id,
        },
        prize_when_buy_with_collection,
      },
      toastOptions: {
        productName: product_name,
        variantName: variant_name,
        regularPrice: regular_price,
        comparePrice: compare_price || 0,
        imageUrl: image_url,
      },
    })

    trackEvent({
      event: ETrackingEvent.AddToCart,
      ecommerce: {
        currency: 'VND',
        value: product.regular_price,
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            price: product.regular_price,
            quantity: 1,
            item_brand: 'Coolmate',
            href: window?.location?.href,
            item_url: window?.location?.href,
            item_image: image_url,
            discount: product.sale_percent,
            item_variant: variant_name,
            item_rating: product?.review?.ratingValue || 0,
          },
        ],
      },
    })

    const ids = product.id + colorSelected.replace('-', '')

    trackPixelEvent('AddToCart', {
      content_name: product.title.replace('/\n/', '').trim(),
      content_category: product?.category,
      content_ids: [ids],
      content_type: 'product',
      value: Number(product.regular_price),
      currency: 'VND',
    })

    trackPixelEvent('InitiateCheckout', {
      content_ids: [ids],
      eventref: '',
    })
  }

  return (
    <ProductDetailContext.Provider
      value={{
        product,
        quantity,
        setQuantity,
        variantActive,
        colorSelected,
        sizeSelected,
        colorOptionActive,
        sizeOptions,
        sortedColorOptions,
        setVariantActive,
        setProduct,
        setColorOptionActive,
        setColorSelected,
        setSizeSelected,
        handleAddToCart,
        isPendingAddToCart,
        comboSelectedInfo,
        setComboSelectedInfo,
      }}
    >
      {children}
    </ProductDetailContext.Provider>
  )
}
